import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import axios from "../../api/axios";

const { Title, Text } = Typography;

type Reseller = {
  id: number;
  name: string;
};

type TopupRow = {
  id?: number;
  createdAt?: string;
  amount: number;
  method?: string;
  reference?: string;
  note?: string;
};

export default function TransactionsPage() {
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [selectedResellerId, setSelectedResellerId] = useState<number | null>(
    null
  );

  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TopupRow[]>([]);

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupLoading, setTopupLoading] = useState(false);
  const [form] = Form.useForm();

  // ✅ Load resellers list
  const loadResellers = async () => {
    try {
      const res = await axios.get("/pos/Admin/resellers");
      setResellers(res.data || []);
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || "Failed to load resellers list"
      );
    }
  };

  const adminGetTopups = (params: {
    resellerId: number;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) => axios.get("/pos/Admin/topups", { params }).then((r) => r.data);

  const adminGetResellerBalance = (resellerId: number) =>
    axios
      .get("/pos/Admin/balance", { params: { resellerId } })
      .then((r) => r.data);

  const adminTopup = (payload: {
    resellerId: number;
    amount: number;
    method?: string;
    reference?: string;
    note?: string;
  }) => axios.post("/pos/Admin/topup", payload).then((r) => r.data);

  const resellerOptions = useMemo(
    () => resellers.map((r) => ({ value: r.id, label: r.name })),
    [resellers]
  );

  const loadAll = async (rid: number) => {
    setLoading(true);
    try {
      const [b, list] = await Promise.all([
        adminGetResellerBalance(rid),
        adminGetTopups({ resellerId: rid, limit: 200, offset: 0 }),
      ]);

      setBalance(typeof b === "number" ? b : Number(b));
      setRows(Array.isArray(list) ? list : list?.items ?? []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResellers();
  }, []);

  useEffect(() => {
    if (selectedResellerId) loadAll(selectedResellerId);
    else {
      setBalance(null);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResellerId]);

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "Type",
      key: "type",
      render: () => "Add funds",
    },
    {
      title: "Amount, €",
      dataIndex: "amount",
      key: "amount",
      render: (v: any) => <b>{Number(v).toFixed(2)}</b>,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      render: (v: any) => v || "-",
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      render: (v: any) => v || "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (v: any) => v || "-",
    },
  ];

  const onSubmitTopup = async () => {
    if (!selectedResellerId) return;

    try {
      const values = await form.validateFields();
      setTopupLoading(true);

      await adminTopup({
        resellerId: selectedResellerId,
        amount: values.amount,
        method: values.method,
        reference: values.reference,
        note: values.note,
      });

      message.success("Funds added");
      setTopupOpen(false);
      form.resetFields();

      await loadAll(selectedResellerId);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || "Topup failed");
    } finally {
      setTopupLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={12}>
          <Title level={3} style={{ margin: 0 }}>
            Transactions
          </Title>
          <Text type="secondary">Admin → Reseller funding history</Text>
        </Col>

        <Col xs={24} md={12}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select reseller"
            options={resellerOptions}
            value={selectedResellerId ?? undefined}
            onChange={(v) => setSelectedResellerId(v)}
            showSearch
            optionFilterProp="label"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <Text type="secondary">Current balance</Text>
            <div style={{ marginTop: 6 }}>
              <Title level={2} style={{ margin: 0 }}>
                {selectedResellerId ? (balance ?? "—") : "—"}
              </Title>
            </div>

            <Button
              type="primary"
              block
              style={{ marginTop: 12 }}
              disabled={!selectedResellerId}
              onClick={() => setTopupOpen(true)}
            >
              Add balance
            </Button>
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card title="Topup transactions">
            <Table
              rowKey={(r) => String(r.id ?? r.createdAt ?? Math.random())}
              columns={columns as any}
              dataSource={rows}
              loading={loading}
              pagination={{ pageSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Add balance"
        open={topupOpen}
        onCancel={() => setTopupOpen(false)}
        onOk={onSubmitTopup}
        confirmLoading={topupLoading}
        okText="Add funds"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <InputNumber
  style={{ width: "100%" }}
  placeholder="e.g. 10 or -10"
  stringMode={false}
/>

          </Form.Item>

          <Form.Item name="method" label="Method">
            <Input placeholder="Cash / Bank / Card / etc." />
          </Form.Item>

          <Form.Item name="reference" label="Reference">
            <Input placeholder="Invoice / receipt / transfer reference" />
          </Form.Item>

          <Form.Item name="note" label="Note">
            <Input.TextArea rows={3} placeholder="Optional note" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
